import Breadcrumbs from '@storeblocks/breadcrumbs';
import Stepper, { Step } from '@storeblocks/stepper';
import { type FC } from 'react';

import { OrderType } from '@/api/order/models/OrderType';
import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { useCurrentUserInfo } from '@/auth/hooks';
import { InternalLink } from '@/components/InternalLink';
import { MainContent } from '@/components/MainContent';
import { PageHeading } from '@/components/PageHeading';
import { Pending } from '@/components/Pending';
import { WithGap } from '@/components/WithGap';
import { isInternalUser } from '@/config/utils';
import { useTexts } from '@/hooks/useTexts';

import { useExistingOrder } from './hooks/useExistingOrder';
import { useOrderPageTitle } from './hooks/useOrderPageTitle';
import { Signed } from './steps/Signed';
import { getOrderPagePath } from './utils/getOrderPagePath';
import { orderHasCosigners } from './utils/orderHasCosigners';

interface Props {
  orderType: OrderType;
}

export const DiscretionaryOrderSignedPage: FC<Props> = ({ orderType }) => {
  const organizationCmId = useOrganizationCmId();
  const texts = useTexts();
  const userInfo = useCurrentUserInfo();

  const pageTitle = useOrderPageTitle(orderType);
  const pagePath = getOrderPagePath(orderType);

  const { data, isFetching } = useExistingOrder();

  if (isFetching || !data) {
    return <Pending center />;
  }

  // Verify that the order type is correct for the current URL since we are
  // using the same page for all the discretionary orders.
  if (data.order.orderType !== orderType) {
    throw new Error(
      `The order is not of the type it should be. The order is [${data.order.orderType}] but should be [${orderType}].`,
    );
  }

  // Internal users (advisors) authenticate via Storebrand SSO and cannot sign
  // orders themselves, so any order they create is always sent to a co-signer
  // and they are always its owner.
  const isOwner =
    isInternalUser() || data.order.initiatorId === userInfo.userCmId;

  // Derive "has co-signers" from the order (signatories vs. the owner), the same
  // way the in-flow completed step does (useHasCosigners), so the message is
  // consistent across both flows and independent of the viewer's CMID.
  const hasCosigners = orderHasCosigners(
    data.order.signatories,
    data.order.initiatorId,
  );

  const steps = isOwner ? 3 : 2;

  return (
    <WithGap>
      <Breadcrumbs expandAllAriaLabel={texts.breadcrumbs.expandAllAriaLabel}>
        <InternalLink to={`/${organizationCmId}`}>
          {texts.menu.links.home}
        </InternalLink>
        <InternalLink to={`/${organizationCmId}/discretionary`}>
          {texts.menu.links.trading.discretionary}
        </InternalLink>
        <InternalLink to={`/${organizationCmId}/discretionary/${pagePath}`}>
          {pageTitle}
        </InternalLink>
      </Breadcrumbs>

      <MainContent>
        <PageHeading header={pageTitle} />

        <Stepper activeStepIndex={steps}>
          {/* Only the owner of the order has access to the order details step. */}
          {isOwner && (
            <Step
              key="details"
              title={texts.orders.form.steps.orderDetails.stepperTitle}
              data-test="order-step-details"
            />
          )}

          <Step
            key="confirm"
            title={texts.orders.form.steps.confirm.stepperTitle}
            data-test="order-step-confirm"
          />

          <Step
            key="signing"
            title={texts.orders.form.steps.signing.stepperTitle}
            data-test="order-step-signing"
          />

          <Step
            key="completed"
            title={texts.orders.form.steps.finished.stepperTitle}
            data-test="order-step-completed"
          >
            <Signed
              orderId={data.order.id}
              isOwner={isOwner}
              hasCosigners={hasCosigners}
              orderType={orderType}
            />
          </Step>
        </Stepper>
      </MainContent>
    </WithGap>
  );
};
